import { i18n } from './create-i18n';
import type { LocaleCode, LocaleMessageOverrides } from './types';

/**
 * Per-kiosk message overrides. Merged on top of base locale messages at
 * runtime via `i18n.global.mergeLocaleMessage(localeCode, overrides)` after
 * `setKioskSlug(slug)`.
 *
 * Each key is a kiosk slug; values are per-locale sparse message overrides.
 * Locales not listed fall back to base messages.
 *
 * NOTE: Typed as `LocaleMessageOverrides` so override structure is
 * statically checked against the base schema. Typos in override keys become
 * compile errors.
 */
export const KIOSK_OVERRIDES: Record<
  string,
  Partial<Record<LocaleCode, LocaleMessageOverrides>>
> = {
  hoh: {
    // GoPhotoUpload.success / .successSubtitle override the generic
    // "Photos Uploaded! / available on your PhotoPass" copy with HoH-
    // specific phrasing (THE-210): at HoH the visitor doesn't carry a
    // PhotoPass — they walk to a GoPhoto pickup station downstairs.
    'en-GB': {
      Home: { title: 'The Masters Await' },
      GoPhotoUpload: {
        success: 'Your photos are on their way!',
        successSubtitle:
          'You can pick them up at the print station downstairs.',
      },
      Portraits: {
        buttonNext: 'Save Selected',
      },
    },
    'en-US': {
      Home: { title: 'The Masters Await' },
      GoPhotoUpload: {
        success: 'Your photos are on their way!',
        successSubtitle:
          'You can pick them up at the print station downstairs.',
      },
      Portraits: {
        buttonNext: 'Save Selected',
      },
    },
    'nl-NL': {
      Home: { title: 'De Meesters Wachten' },
      GoPhotoUpload: {
        success: "Je foto's zijn onderweg!",
        successSubtitle: 'Je kunt ze ophalen bij het printstation beneden.',
      },
      Portraits: {
        buttonNext: 'Bewaar selectie',
      },
    },
    'de-DE': { Home: { title: 'Die Meister Erwarten Euch' } },
    'fr-FR': { Home: { title: 'Les Maîtres Vous Attendent' } },
    'it-IT': { Home: { title: 'I Maestri Vi Attendono' } },
    'es-ES': { Home: { title: 'Los Maestros Os Esperan' } },
    'pt-PT': { Home: { title: 'Os Mestres Vos Aguardam' } },
    'ru-RU': { Home: { title: 'Мастера Ожидают Вас' } },
    'zh-CN': { Home: { title: '大师恭候您的到来' } },
  },
};

/**
 * Apply per-kiosk message overrides. Safe to call multiple times. Overrides
 * are deep-merged via `i18n.global.mergeLocaleMessage` — keys not in the
 * override tree fall back to base messages.
 */
export function applyKioskOverrides(slug: string | null | undefined): void {
  if (!slug) return;
  const overrides = KIOSK_OVERRIDES[slug];
  if (!overrides) return;
  for (const [localeCode, messages] of Object.entries(overrides)) {
    if (!messages) continue;
    i18n.global.mergeLocaleMessage(
      localeCode as LocaleCode,
      messages as Record<string, unknown>
    );
  }
}
